import React from 'react';
import { executeBackendQuery } from '../../requests/executeBackendQuery';
import { VENDOR_LIST } from '../../consts/queries';
import filter from '../../filter';
import BulkPicker from '../BulkPicker/BulkPicker';
import {
    getVendorDisplayName,
    getVendorCompanyName,
    sortVendorsAlphabetically,
} from '../SelectEscrowOfficer/SelectEscrowOfficer';

const STATE_CODES = {
    alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA', colorado: 'CO',
    connecticut: 'CT', delaware: 'DE', 'district of columbia': 'DC', florida: 'FL', georgia: 'GA',
    hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY',
    louisiana: 'LA', maine: 'ME', maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
    mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH',
    'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
    ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
    'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
    virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
};

/** "Texas", "texas" and " tx " all become "TX"; unknown text is kept upper-cased. */
export function normalizeState(value) {
    const text = String(value || '').trim().replace(/\s+/g, ' ');
    if (!text) return '';
    return STATE_CODES[text.toLowerCase()] || text.toUpperCase();
}

// The contact's own company state, else its firm's: some contacts carry it only on the firm.
const stateOf = (vendor) => normalizeState(vendor.COMPANY_STATE || vendor.FIRM_STATE);

/** The lender object a row stores; the same shape LoanBidRow builds on a single pick. */
export function toLender(vendor) {
    return {
        vendor_id: String(vendor.ARCIMEDES_ROW_ID),
        name: getVendorDisplayName(vendor),
        lender: getVendorCompanyName(vendor),
        work_phone: vendor.WORK_PHONE || null,
        cell_phone: vendor.PHONE || null,
        email: vendor.EMAIL_ID || null,
        crm_lead_id: vendor.LEAD_ID || null,
    };
}

const idOf = (v) => String(v.ARCIMEDES_ROW_ID);

/**
 * Bulk add for Loan Bids: pick many lender contacts at once, filtered by
 * state or text. Loads contacts the way SelectEscrowOfficer does; the panel
 * itself is the shared BulkPicker. A native select is used so nothing renders
 * in a popper that would have to be lifted above the dialog.
 */
export default function LenderBulkPicker({ takenIds, onAdd, onCancel }) {
    const [vendors, setVendors] = React.useState(null);
    const [failed, setFailed] = React.useState(false);
    const [state, setState] = React.useState('');
    const [query, setQuery] = React.useState('');

    React.useEffect(() => {
        let live = true;
        const options = filter({ VENDOR_TYPE: 'Lender' }, 'vendor');
        options.param.limit = 5000;
        options.param.offset = 0;
        executeBackendQuery({ queryName: VENDOR_LIST, options, expireTime: 300 })
            .then((res) => {
                if (!live) return;
                setVendors(sortVendorsAlphabetically((res && res.data || []).filter((v) => v.DELETED !== '1')));
            })
            .catch(() => { if (live) setFailed(true); });
        return () => { live = false; };
    }, []);

    const taken = new Set((takenIds || []).map(String));
    // A contact without a row id cannot be stored on the list.
    const available = (vendors || []).filter((v) => v.ARCIMEDES_ROW_ID && !taken.has(String(v.ARCIMEDES_ROW_ID)));
    const states = [...new Set(available.map(stateOf).filter(Boolean))].sort();

    const q = query.trim().toLowerCase();
    const shown = available.filter((v) => {
        if (state && stateOf(v) !== state) return false;
        if (!q) return true;
        return (getVendorDisplayName(v) + ' ' + getVendorCompanyName(v)).toLowerCase().includes(q);
    });

    // Added in the list's alphabetical order, not the order they were ticked,
    // and only while still available, so a stale tick never adds a duplicate.
    const add = (picked) => {
        const ids = new Set(picked.map(idOf));
        onAdd(available.filter((v) => ids.has(idOf(v))).map(toLender));
    };

    return (
        <BulkPicker
            classPrefix="LoanBids"
            testId="loan-bids-bulk"
            groupLabel="Lender contacts"
            filters={(
                <React.Fragment>
                    <select
                        className="LoanBids-bulkState"
                        aria-label="State"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                    >
                        <option value="">All states</option>
                        {states.map((code) => <option key={code} value={code}>{code}</option>)}
                    </select>
                    <input
                        type="text"
                        className="LoanBids-bulkSearch"
                        placeholder="Search name or lender..."
                        aria-label="Search name or lender"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </React.Fragment>
            )}
            items={shown}
            idOf={idOf}
            takenIds={takenIds}
            loading={vendors === null}
            failed={failed}
            failedText="Could not load lender contacts."
            emptyText="No contacts match."
            renderItem={(v) => (
                <React.Fragment>
                    <span className="LoanBids-bulkName">{getVendorDisplayName(v)}</span>
                    <span className="LoanBids-bulkLender">{getVendorCompanyName(v) || '—'}</span>
                    <span className="LoanBids-bulkSt">{stateOf(v) || '—'}</span>
                </React.Fragment>
            )}
            onAdd={add}
            onCancel={onCancel}
        />
    );
}
